import { clientBaseOps, Client } from '@basehub/data/replicache/index'
import { clientStaleTimeMs } from '@basehub/shared-constants'

import type { ReadTransaction, WriteTransaction } from 'replicache'
clientStaleTimeMs

/**
 * Will list all clients and filter them by the ones that were active in the last 10 seconds
 * (10 seconds cause clients will ping every 5 seconds)
 */
const listActiveClients = async (
  tx: ReadTransaction,
  data: { now: number; branchId: string }
) => {
  const clients = await clientBaseOps.list(tx)

  return clients
    .filter((c) => {
      return (
        c.branchId === data.branchId &&
        c.lastActiveAt > data.now - clientStaleTimeMs
      )
    })
    .sort((a, b) => (a.createdAt > b.createdAt ? -1 : 1))
}

/**
 * Initialize yourself. Should be triggered on initReplicache
 */
const initClient = async (
  tx: WriteTransaction,
  data: {
    branchId: string
    id: string
    userId: string
    color: string
    now: number
    killDuplicateClientId?: string | null
  }
) => {
  let deleteClientPromise: Promise<void> | null = null
  if (data.killDuplicateClientId) {
    deleteClientPromise = clientBaseOps.delete(tx, data.killDuplicateClientId)
  }

  const existingClient = await clientBaseOps.get(tx, data.id)
  if (existingClient) {
    const updateClientPromise = clientBaseOps.update(tx, {
      id: data.id,
      lastActiveAt: data.now,
      branchId: data.branchId,
    })

    await Promise.all([updateClientPromise, deleteClientPromise])

    return { ...existingClient, lastActiveAt: data.now }
  }

  const putClientPromise = clientBaseOps.put(tx, {
    branchId: data.branchId,
    id: data.id,
    userId: data.userId,
    color: data.color,
    createdAt: data.now,
    lastActiveAt: data.now,
    selection: null,
  })

  await Promise.all([putClientPromise, deleteClientPromise])
}

const updateClientFocus = async (
  tx: WriteTransaction,
  data: {
    id: string
    selection: Client['selection']
    now: number
    branchId: string
  }
) => {
  await clientBaseOps.update(tx, {
    id: data.id,
    selection: data.selection,
    lastActiveAt: data.now,
    branchId: data.branchId,
  })
}

const keepClientAlive = async (
  tx: WriteTransaction,
  data: { id: string; now: number; branchId: string }
) => {
  await clientBaseOps.update(tx, {
    id: data.id,
    lastActiveAt: data.now,
    branchId: data.branchId,
  })
}

// exports
export const clientMutators = {
  listActiveClients,
  initClient,
  updateClientFocus,
  keepClientAlive,
}
